import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePostHog } from "@posthog/react";
import { getApiBase } from "../utils/apiBase";
import { ensureServerAwake } from "../utils/wakeServer";
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import {
  firebaseAuth,
  googleProvider,
} from "../auth/firebaseClient";

/** Persist across tab close / browser restart (unlike sessionStorage, which clears with the tab). */
const STORAGE_KEY = "addata_auth_token";
const SEEN_USERS_KEY = "addata_seen_users_v1";

function readStoredAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  const fromLocal = localStorage.getItem(STORAGE_KEY);
  if (fromLocal) return fromLocal;
  const fromSession = sessionStorage.getItem(STORAGE_KEY);
  if (fromSession) {
    localStorage.setItem(STORAGE_KEY, fromSession);
    sessionStorage.removeItem(STORAGE_KEY);
    return fromSession;
  }
  return null;
}

export type AuthUser = {
  userId: string;
  displayName: string;
  email: string | null;
  picture: string | null;
};

function readSeenUsers(): Record<string, true> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(SEEN_USERS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, true>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSeenUser(userId: string): boolean {
  if (typeof window === "undefined") return false;
  const seen = readSeenUsers();
  const isFirst = !seen[userId];
  if (isFirst) {
    seen[userId] = true;
    localStorage.setItem(SEEN_USERS_KEY, JSON.stringify(seen));
  }
  return isFirst;
}

/** From GET /api/me — resumable lobby or in-progress seat (signed-in only). */
export type ActiveRoomInfo = {
  namespace: string;
  roomId: string;
  playerUuid: string;
  gameTitle: string;
  phase: "lobby" | "playing";
};

type UserContextValue = {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  /** Last known in-progress or lobby seat from the server (may be empty). */
  activeRooms: ActiveRoomInfo[];
  isFirstLogin: boolean;
  signInWithGoogleToken: (credential: string) => Promise<void>;
  signInWithEmailPassword: (email: string, password: string) => Promise<void>;
  signUpWithEmailPassword: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  /** Firebase password reset email (works for email/password accounts). */
  sendPasswordResetEmail: (email: string) => Promise<void>;
  signOut: () => void;
  refreshProfile: () => Promise<void>;
  /** Store a new JWT (e.g. after PATCH /api/me updates display name in token claims). */
  refreshAuthToken: (newToken: string) => void;
};

const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const posthog = usePostHog();
  const [token, setToken] = useState<string | null>(() => readStoredAuthToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [activeRooms, setActiveRooms] = useState<ActiveRoomInfo[]>([]);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [loading, setLoading] = useState(true);

  const persistToken = useCallback((t: string | null) => {
    setToken(t);
    if (typeof localStorage === "undefined") return;
    if (t) localStorage.setItem(STORAGE_KEY, t);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const fetchMe = useCallback(async (authToken: string) => {
    await ensureServerAwake();
    const res = await fetch(`${getApiBase()}/api/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) {
      const err = new Error(`GET /api/me failed: ${res.status}`);
      posthog?.capture("api_me_failed", { http_status: res.status });
      posthog?.captureException(err, { source: "api_me", http_status: res.status });
      throw err;
    }
    const data = (await res.json()) as AuthUser & { activeRooms?: ActiveRoomInfo[] };
    setUser({
      userId: data.userId,
      displayName: data.displayName,
      email: data.email,
      picture: data.picture,
    });
    setActiveRooms(Array.isArray(data.activeRooms) ? data.activeRooms : []);
      setIsFirstLogin(!readSeenUsers()[data.userId]);
  }, [posthog]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setUser(null);
        setActiveRooms([]);
        setLoading(false);
        return;
      }
      try {
        await fetchMe(token);
      } catch {
        if (!cancelled) {
          posthog?.capture("auth_session_restore_failed", { had_token: true });
          persistToken(null);
          setUser(null);
          setActiveRooms([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchMe, persistToken, posthog]);

  // Identify authenticated users on session restore (page reload with stored token)
  useEffect(() => {
    if (user) {
      posthog?.identify(user.userId, {
        name: user.displayName,
      });
    }
  }, [posthog, user]);

  const exchangeFirebaseToken = useCallback(
    async (idToken: string) => {
      await ensureServerAwake();
      const res = await fetch(`${getApiBase()}/api/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as {
          error?: string;
          details?: string;
        };
        const msg = [err.details, err.error].filter(Boolean).join(": ") || "Sign-in failed";
        const authError = new Error(msg);
        posthog?.captureException(authError, { level: "error" });
        throw authError;
      }
      const data = (await res.json()) as { token: string; user: AuthUser };
      persistToken(data.token);
      setUser(data.user);
      setIsFirstLogin(writeSeenUser(data.user.userId));
      posthog?.identify(data.user.userId, {
        name: data.user.displayName,
      });
      posthog?.capture("user_signed_in_with_firebase");
      await fetchMe(data.token);
    },
    [persistToken, fetchMe, posthog]
  );

  const signInWithGoogleToken = useCallback(
    async (_credential: string) => {
      throw new Error("Google OAuth credential flow is disabled. Use Firebase social sign-in.");
    },
    []
  );

  const signInWithEmailPassword = useCallback(
    async (email: string, password: string) => {
      if (!firebaseAuth) throw new Error("Firebase is not configured");
      const out = await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
      const idToken = await out.user.getIdToken(true);
      await exchangeFirebaseToken(idToken);
    },
    [exchangeFirebaseToken]
  );

  const signUpWithEmailPassword = useCallback(
    async (email: string, password: string, displayName: string) => {
      if (!firebaseAuth) throw new Error("Firebase is not configured");
      const out = await createUserWithEmailAndPassword(firebaseAuth, email.trim(), password);
      if (displayName.trim().length > 0) {
        await updateProfile(out.user, { displayName: displayName.trim() });
      }
      const idToken = await out.user.getIdToken(true);
      await exchangeFirebaseToken(idToken);
    },
    [exchangeFirebaseToken]
  );

  const signInWithGoogle = useCallback(
    async () => {
      if (!firebaseAuth) throw new Error("Firebase is not configured");
      const out = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await out.user.getIdToken(true);
      await exchangeFirebaseToken(idToken);
    },
    [exchangeFirebaseToken]
  );

  const sendPasswordResetEmailFn = useCallback(async (email: string) => {
    if (!firebaseAuth) throw new Error("Firebase is not configured");
    const trimmed = email.trim();
    if (!trimmed) throw new Error("auth/missing-email");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // ActionCodeSettings.url must be a string — never pass `undefined` (use two-arg overload instead).
    if (origin.length > 0) {
      await firebaseSendPasswordResetEmail(firebaseAuth, trimmed, { url: `${origin}/` });
    } else {
      await firebaseSendPasswordResetEmail(firebaseAuth, trimmed);
    }
  }, []);

  const signOut = useCallback(() => {
    posthog?.capture("user_signed_out");
    posthog?.reset();
    persistToken(null);
    setUser(null);
    setActiveRooms([]);
    setIsFirstLogin(false);
  }, [persistToken, posthog]);

  const refreshProfile = useCallback(async () => {
    if (!token) return;
    await fetchMe(token);
  }, [token, fetchMe]);

  const refreshAuthToken = useCallback(
    (newToken: string) => {
      persistToken(newToken);
    },
    [persistToken]
  );

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      activeRooms,
      isFirstLogin,
      signInWithGoogleToken,
      signInWithEmailPassword,
      signUpWithEmailPassword,
      signInWithGoogle,
      sendPasswordResetEmail: sendPasswordResetEmailFn,
      signOut,
      refreshProfile,
      refreshAuthToken,
    }),
    [
      user,
      token,
      loading,
      activeRooms,
      isFirstLogin,
      signInWithGoogleToken,
      signInWithEmailPassword,
      signUpWithEmailPassword,
      signInWithGoogle,
      sendPasswordResetEmailFn,
      signOut,
      refreshProfile,
      refreshAuthToken,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextValue => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
};
